import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { uniqBy } from 'lodash';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import { Field } from 'redux-form';

import { changePatientResource } from 'redux/modules/patients';

import List from '@material-ui/core/List';

import OrderInformationSection from 'components/workflow/OrderInformation/OrderInformationSection';
import OrderInformationSectionTitle from 'components/workflow/OrderInformation/OrderInformationSectionTitle';
import OrderInformationSectionEmpty from 'components/workflow/OrderInformation/OrderInformationSectionEmpty';
import DiagnosesAddDialog from 'components/patient/PatientProfile/Diagnoses/DiagnosesAddDialog';
import OrderInformationDiagnosesMenu from './OrderInformationDiagnosesMenu';
import OrderInformationDiagnosesField from './OrderInformationDiagnosesField';

import dateFormat from 'config/dateFormat';

const styles = () => ({
  list: {
    position: 'relative',
  },
});

class OrderInformationDiagnoses extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dialogOpen: false,
      anchorEl: null,
      offsetFields: [],
      draggableData: {},
      dragged: false,
      timestamp: Date.parse(new Date()),
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onAddButtonClick = this.onAddButtonClick.bind(this);
    this.onCreateButtonClick = this.onCreateButtonClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onFieldDragStart = this.onFieldDragStart.bind(this);
    this.onFieldDrag = this.onFieldDrag.bind(this);
    this.onFieldDragStop = this.onFieldDragStop.bind(this);
    this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onAddButtonClick(event) {
    const { Patient, fields } = this.props;
    const diagnosesToAdd = fields.length > 0
      ? Patient.Diagnosis
        .filter(item => fields.getAll().indexOf(item.ID) === -1)
      : Patient.Diagnosis;
    if (diagnosesToAdd.length > 0) {
      this.setState({ anchorEl: event.currentTarget });
    } else {
      this.setState({ dialogOpen: true });
    }
  }

  onCreateButtonClick() {
    this.setState({
      dialogOpen: true,
      anchorEl: null,
    });
  }

  onDialogClose() {
    this.setState({ dialogOpen: false });
  }

  onDraggableDataChange(data) {
    this.setState({
      draggableData: {
        ...this.state.draggableData,
        ...data,
      },
    });
  }

  onFieldDragStart() {
    this.setState({ dragged: true });
  }

  onFieldDrag(data) {
    const { fields } = this.props;
    const { draggableData } = this.state;
    let moveDirectionY;
    if (Math.abs(data.defaultY - data.y) > 20) {
      moveDirectionY = data.y > data.defaultY ? 'bottom' : 'top';
    }
    const offsetFields = fields.reduce((accumulator, field, index) => {
      if (index === data.index) {
        return accumulator;
      }
      if (moveDirectionY === 'bottom') {
        if (index > data.index
          && draggableData[index] !== undefined
          && ((draggableData[index].height / 2) - draggableData[index].bounds.top)
          < (data.y + data.height)) {
          return [
            ...accumulator,
            {
              fieldIndex: index,
              offsetY: -data.height,
            },
          ];
        }
        return accumulator;
      }
      if (moveDirectionY === 'top') {
        if (index < data.index
          && draggableData[index] !== undefined
          && ((draggableData[index].height / 2) - draggableData[index].bounds.top)
          > data.y) {
          return [
            ...accumulator,
            {
              fieldIndex: index,
              offsetY: data.height,
            },
          ];
        }
        return accumulator;
      }
      return accumulator;
    }, []);
    this.setState({ offsetFields });
  }

  onFieldDragStop(data) {
    const { fields } = this.props;
    const { offsetFields } = this.state;
    if (offsetFields.length > 0) {
      let moveDirectionY;
      let moveIndex;
      if (Math.abs(data.defaultY - data.y) > 20) {
        moveDirectionY = data.y > data.defaultY ? 'bottom' : 'top';
      }
      this.setState({
        dragged: false,
        offsetFields: [],
      });
      if (moveDirectionY === 'top') {
        moveIndex = offsetFields[0].fieldIndex;
      }
      if (moveDirectionY === 'bottom') {
        moveIndex = offsetFields[offsetFields.length - 1].fieldIndex;
      }
      if (moveIndex !== undefined) {
        fields.move(data.index, moveIndex);
        this.setState({ timestamp: Date.parse(new Date()) });
      }
    }
  }

  render() {
    const { fields, Patient, Diagnosis, actions, formValues, disabled, classes } = this.props;
    const { anchorEl, dialogOpen, dragged, offsetFields, timestamp } = this.state;
    const menuOpen = Boolean(anchorEl);
    const diagnosesToAdd = fields.length > 0
      ? Patient.Diagnosis
        .filter(item => fields.getAll().indexOf(item.ID) === -1)
      : Patient.Diagnosis;
    const dateOfServiceTo = (formValues && formValues.orderLine && formValues.orderLine[0])
      ? formValues.orderLine.reduce((accumulator, currentItem) => {
        if (moment(currentItem.dateOfServiceTo, dateFormat).isAfter(moment(accumulator, dateFormat))) {
          return currentItem.dateOfServiceTo;
        }
        return accumulator;
      }, formValues.orderLine[0].dateOfServiceTo) : moment().format(dateFormat);
    return (
      <OrderInformationSection>
        {fields.length > 0 ? (
          <Fragment>
            <OrderInformationSectionTitle
              title="Diagnoses"
              addButtonOwns="order-information-add-diagnoses-menu"
              onAddButtonClick={this.onAddButtonClick}
              addButtonDisabled={fields.length >= 12 || disabled}
            />
            <List className={classes.list} disablePadding>
              {fields.map((diagnosesItem, index) => {
                const offsetFieldIndex = offsetFields
                  .findIndex(item => (item.fieldIndex === index));
                return (
                  <Field
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    index={index}
                    name={diagnosesItem}
                    pointer={'ABCDEFGHIJKL'.slice(index, index + 1)}
                    component={OrderInformationDiagnosesField}
                    Diagnosis={uniqBy([
                      ...Patient.Diagnosis || [],
                      ...Diagnosis || [],
                    ], 'ID')}
                    onRemove={() => fields.remove(index)}
                    disabled={disabled}
                    dateOfServiceTo={dateOfServiceTo}
                    {...(fields.length > 1)
                      ? {
                        boundsSelector: `.${classes.list}`,
                        onStartDrag: this.onFieldDragStart,
                        onDrag: this.onFieldDrag,
                        onStopDrag: this.onFieldDragStop,
                        onDraggableDataChange: this.onDraggableDataChange,
                        withTransition: dragged,
                        timestamp,
                        ...offsetFieldIndex !== -1
                          ? {
                            offsetY: offsetFields[offsetFieldIndex].offsetY,
                            offsetX: offsetFields[offsetFieldIndex].offsetX,
                          } : {},
                      }
                      : {
                        dragDisabled: true,
                      }}
                  />
                );
              })}
            </List>
          </Fragment>
        ) : (
          <OrderInformationSectionEmpty
            title="Diagnoses"
            text="There are no diagnoses"
            type="diagnoses"
            addButtonOwns="order-information-add-diagnoses-menu"
            onAddButtonClick={this.onAddButtonClick}
          />
        )}
        {diagnosesToAdd.length > 0 &&
          <OrderInformationDiagnosesMenu
            open={menuOpen}
            anchorEl={anchorEl}
            onClose={this.onMenuClose}
            id="order-information-add-diagnoses-menu"
            onItemClick={(item) => {
              this.onMenuClose();
              fields.push(item.ID);
            }}
            onCreateButtonClick={this.onCreateButtonClick}
            Diagnosis={diagnosesToAdd}
          />
        }
        <DiagnosesAddDialog
          open={dialogOpen}
          onClose={this.onDialogClose}
          onSubmitSuccess={(values) => {
            this.onDialogClose();
            new Promise((resolve, reject) => {
              actions.changePatientResource({
                'Record Number': Patient['Record Number'],
                'Diagnosis ID': [
                  ...Patient.Diagnosis.map(item => item.ID),
                  values.ID,
                ],
                resolve,
                reject,
              });
            }).then(() => {
              if (fields.getAll() && fields.getAll().indexOf(values.ID) === -1) {
                fields.push(values.ID);
              }
            });
          }}
          formValues={formValues}
        />
      </OrderInformationSection>
    );
  }
}

OrderInformationDiagnoses.propTypes = {
  fields: PropTypes.shape({
    name: PropTypes.string.isRequired,
    remove: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  }).isRequired,
  Diagnosis: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  actions: PropTypes.shape({
    changePatientResource: PropTypes.func.isRequired,
  }).isRequired,
  formValues: PropTypes.shape({
    'Primary Insurance ID': PropTypes.number,
  }),
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    list: PropTypes.string.isRequired,
  }).isRequired,
};

OrderInformationDiagnoses.defaultProps = {
  Diagnosis: null,
  formValues: {},
  disabled: false,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ changePatientResource }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(OrderInformationDiagnoses),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrderInformation/OrderInformationDiagnoses/OrderInformationDiagnoses.js